import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './icons';

// Small building blocks shared by the views of the admin panel.

// ---------------------------------------------------------------------------
// VirtualList: fixed-height rows, only the visible ones (plus a margin) are in
// the DOM, so a roster of thousands of characters scrolls without a hitch.
// Keyboard: arrows, Page Up/Down, Home/End move the cursor; Enter activates.
// ---------------------------------------------------------------------------

interface VirtualListProps<T> {
  items: T[];
  rowHeight: number;
  getKey: (item: T) => string | number;
  renderRow: (item: T, index: number, state: { cursor: boolean }) => React.ReactNode;
  cursor: number;
  onCursorChange: (index: number) => void;
  onActivate?: (item: T, index: number) => void;
  ariaLabel: string;
  idPrefix: string;
  className?: string;
  empty?: React.ReactNode;
}

const OVERSCAN = 6;

export function VirtualList<T>(props: VirtualListProps<T>): React.ReactElement {
  const { items, rowHeight, getKey, renderRow, cursor, onCursorChange, onActivate, ariaLabel, idPrefix, className, empty } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [height, setHeight] = useState(400);
  const raf = useRef(0);

  const measure = useCallback(() => {
    const el = ref.current;
    if (el && el.clientHeight && el.clientHeight !== height) setHeight(el.clientHeight);
  }, [height]);

  useLayoutEffect(measure);

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.cancelAnimationFrame(raf.current);
    };
  }, [measure]);

  const onScroll = () => {
    if (raf.current) return;
    raf.current = window.requestAnimationFrame(() => {
      raf.current = 0;
      if (ref.current) setScrollTop(ref.current.scrollTop);
    });
  };

  // Keep the cursor row on screen
  useEffect(() => {
    const el = ref.current;
    if (!el || cursor < 0) return;
    const top = cursor * rowHeight;
    if (top < el.scrollTop) el.scrollTop = top;
    else if (top + rowHeight > el.scrollTop + el.clientHeight) el.scrollTop = top + rowHeight - el.clientHeight;
  }, [cursor, rowHeight]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!items.length) return;
    const page = Math.max(1, Math.floor(height / rowHeight) - 1);
    let next = cursor;
    if (e.key === 'ArrowDown') next = cursor < 0 ? 0 : Math.min(items.length - 1, cursor + 1);
    else if (e.key === 'ArrowUp') next = cursor < 0 ? 0 : Math.max(0, cursor - 1);
    else if (e.key === 'PageDown') next = Math.min(items.length - 1, Math.max(0, cursor) + page);
    else if (e.key === 'PageUp') next = Math.max(0, Math.max(0, cursor) - page);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else if (e.key === 'Enter' && cursor >= 0 && cursor < items.length) {
      e.preventDefault();
      if (onActivate) onActivate(items[cursor], cursor);
      return;
    } else return;
    e.preventDefault();
    if (next !== cursor) onCursorChange(next);
  };

  if (!items.length) {
    return <div className={className}>{empty}</div>;
  }

  const first = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN);
  const last = Math.min(items.length, Math.ceil((scrollTop + height) / rowHeight) + OVERSCAN);
  const rows: React.ReactNode[] = [];
  for (let i = first; i < last; i++) {
    const item = items[i];
    rows.push(
      <div
        key={getKey(item)}
        id={`${idPrefix}-${i}`}
        role="option"
        aria-selected={i === cursor}
        className="admin-panel__vrow"
        style={{ top: i * rowHeight, height: rowHeight }}
        onClick={() => {
          onCursorChange(i);
          if (onActivate) onActivate(item, i);
        }}
      >
        {renderRow(item, i, { cursor: i === cursor })}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={'admin-panel__vlist ' + (className || '')}
      role="listbox"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-activedescendant={cursor >= 0 ? `${idPrefix}-${cursor}` : undefined}
      onScroll={onScroll}
      onKeyDown={onKeyDown}
    >
      <div style={{ position: 'relative', height: items.length * rowHeight }}>{rows}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Switch, segmented control, empty state, spinner, confirmation dialog
// ---------------------------------------------------------------------------

export const Switch: React.FC<{ checked: boolean; onChange: (next: boolean) => void; label: string; disabled?: boolean }> = ({ checked, onChange, label, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    className={'admin-panel__switch' + (checked ? ' admin-panel__switch--on' : '')}
    onClick={() => onChange(!checked)}
  >
    <span className="admin-panel__switch-knob" />
  </button>
);

export function Segmented<T extends string>(props: { value: T; options: Array<{ id: T; label: string; count?: number }>; onChange: (v: T) => void; label: string }): React.ReactElement {
  const { value, options, onChange, label } = props;
  return (
    <div className="admin-panel__segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={o.id === value}
          className={'admin-panel__seg' + (o.id === value ? ' admin-panel__seg--active' : '')}
          onClick={() => onChange(o.id)}
        >
          {o.label}
          {o.count != null ? <span className="admin-panel__seg-count">{o.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export const Empty: React.FC<{ title: string; text?: string; children?: React.ReactNode }> = ({ title, text, children }) => (
  <div className="admin-panel__empty-state">
    <div className="admin-panel__empty-title">{title}</div>
    {text ? <div className="admin-panel__empty-text">{text}</div> : null}
    {children}
  </div>
);

// Loading placeholders that take the shape of what is coming, instead of a generic spinner
export type SkeletonShape = 'rows' | 'stats' | 'cards' | 'form';

const bars = (n: number, cls: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  for (let i = 0; i < n; i++) out.push(<span key={i} className={'admin-panel__skel ' + cls} style={{ width: 55 + ((i * 17) % 40) + '%' }} />);
  return out;
};

export const Loading: React.FC<{ text: string; shape?: SkeletonShape }> = ({ text, shape = 'rows' }) => (
  <div className={'admin-panel__skeleton admin-panel__skeleton--' + shape} role="status" aria-live="polite" aria-label={text}>
    <span className="admin-panel__sr">{text}</span>
    {shape === 'stats'
      ? [0, 1, 2, 3].map((i) => (
          <div key={i} className="admin-panel__skel-stat" aria-hidden="true">
            <span className="admin-panel__skel admin-panel__skel--label" />
            <span className="admin-panel__skel admin-panel__skel--value" />
            <span className="admin-panel__skel admin-panel__skel--label" />
          </div>
        ))
      : shape === 'cards'
      ? [0, 1, 2, 3].map((i) => (
          <div key={i} className="admin-panel__skel-card" aria-hidden="true">
            {bars(3, 'admin-panel__skel--line')}
          </div>
        ))
      : shape === 'form'
      ? bars(7, 'admin-panel__skel--field')
      : [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="admin-panel__skel-row" aria-hidden="true">
            <span className="admin-panel__skel admin-panel__skel--dot" />
            <span className="admin-panel__skel admin-panel__skel--line" style={{ width: 120 + ((i * 37) % 90) }} />
            <span className="admin-panel__skel admin-panel__skel--line admin-panel__skel--grow" />
          </div>
        ))}
  </div>
);

export const IconButton: React.FC<{ icon: string; label: string; onClick: () => void; active?: boolean; disabled?: boolean }> = ({ icon, label, onClick, active, disabled }) => (
  <button
    type="button"
    className={'admin-panel__iconbtn' + (active ? ' admin-panel__iconbtn--active' : '')}
    aria-label={label}
    title={label}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
  >
    <Icon name={icon} size={16} />
  </button>
);

export const ViewHeader: React.FC<{ title: string; subtitle?: string; children?: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div className="admin-panel__view-head">
    <div className="admin-panel__view-titles">
      <h2 className="admin-panel__view-title">{title}</h2>
      {subtitle ? <p className="admin-panel__view-sub">{subtitle}</p> : null}
    </div>
    {children ? <div className="admin-panel__view-tools">{children}</div> : null}
  </div>
);

export const ConfirmDialog: React.FC<{
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ title, body, confirmLabel, danger, onConfirm, onCancel }) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Cancel has the focus first: Enter alone never confirms a destructive action; the focus goes back
  // to the control that opened the dialog when it closes
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    if (cancelRef.current) cancelRef.current.focus();
    return () => {
      if (opener && opener.focus && document.contains(opener)) opener.focus();
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const active = document.activeElement;
    if (e.shiftKey && active === cancelRef.current) {
      e.preventDefault();
      if (confirmRef.current) confirmRef.current.focus();
    } else if (!e.shiftKey && active === confirmRef.current) {
      e.preventDefault();
      if (cancelRef.current) cancelRef.current.focus();
    }
  };

  return (
    <div className="admin-panel__modal-backdrop" onKeyDown={onKeyDown}>
      <div className="admin-panel__modal" role="alertdialog" aria-modal="true" aria-labelledby="ap-confirm-title" aria-describedby="ap-confirm-body">
        <h3 id="ap-confirm-title" className="admin-panel__modal-title">{title}</h3>
        <div id="ap-confirm-body" className="admin-panel__modal-body">{body}</div>
        <div className="admin-panel__modal-actions">
          <button ref={cancelRef} type="button" className="admin-panel__button" onClick={onCancel}>Annuler</button>
          <button
            ref={confirmRef}
            type="button"
            className={'admin-panel__button ' + (danger ? 'admin-panel__button--danger-solid' : 'admin-panel__button--primary')}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
